import * as React from 'react';
import {
	GOAL_Amount,
	GOAL_goalId,
	GOAL_GoalType,
	GOAL_TargetAmount,
	UPDATE_GOAL_DATA
} from './goal.constant';
import { goalContextReducer, GoalContextState, initialState } from './goal.context.reducer';
import { TLeadGoalsResponse } from '../../models/goals-info-response';
import { parseOtherIncome } from '../../helpers/goal.helper';
import { useEffect } from 'react';
import { LocalStorageUtils } from '@assetmark/ui-kit/dist/utils/local-storage.utils';
import { GOAL_ID_KEY, GOAL_KEY } from '../../constants/storage.constant';

type LeadProviderProps = { children: React.ReactNode };

const GoalStateContext = React.createContext<GoalContextState>(initialState);
GoalStateContext.displayName = 'GoalContext';

function GoalContextProvider({ children }: LeadProviderProps) {
	const [state, dispatch] = React.useReducer(goalContextReducer, initialState);
	
	useEffect(() => {
		const storageGoalId = LocalStorageUtils.getItem<string>(GOAL_ID_KEY);
		updateGoalId(storageGoalId.getOrElse(''));
	}, []);

	const updateGoalData = (goal: TLeadGoalsResponse) => {
		const copyGoals = Object.assign({}, state.goals);
		goal.id.fold('', id => (copyGoals['GoalId'] = id));
		goal.annualSavings.fold(0, value => (copyGoals['AnnualSavings'] = value));
		goal.investmentAmount.fold(0, value => (copyGoals['Amount'] = value));
		goal.birthYear.fold(0, value => (copyGoals['BirthYear'] = value));
		//goal.investmentTypeEnum.fold(0, value => (copyGoals['GoalType'] =  Number(value)));
		goal.targetGoalYear.fold(0, value => (copyGoals['TargetYear'] = value));
		goal.targetGrowthAmount.fold(0, value => (copyGoals['TargetAmount'] = value));
		goal.monthlyIncome.fold(0, value => (copyGoals['MonthlyIncome'] = value));
		goal.monthlyIncomeToRetirePercent.fold(0, value => (copyGoals['MonthlyIncomeToRetirePercent'] = value));
		goal.netRetirementMonthlyIncome.fold(0, value => copyGoals['NetRetirementMonthlyIncome'] = value);
		goal.leadOtherRetirementIncome.fold(
			[],
			value =>
				(copyGoals['OtherRetirementIncomes'] = parseOtherIncome(value)),
		);
		if (goal.birthYear.isSome() && goal.targetGoalYear.isSome()) {
			if (goal.birthYear.getOrElse(0) !== 0 && goal.targetGoalYear.getOrElse(0) !== 0) {
				copyGoals['retirementAge'] = goal.targetGoalYear.getOrElse(0) - goal.birthYear.getOrElse(0);
			}
		}
		dispatch({ type: UPDATE_GOAL_DATA, payload: copyGoals });
		updateGoalId(goal.id.getOrElse(''));
	};
	
	const updateGoalId = (goalId: string) => dispatch({ type: GOAL_goalId, payload: goalId });
	const goalAmount = (amount: number) => { 
		dispatch({ type: GOAL_Amount, payload: amount }); 
	};
	const goalGoalType = (goalType: number) => dispatch({ type: GOAL_GoalType, payload: goalType });
	const goalTargetAmount = (amount: number) => dispatch({ type: GOAL_TargetAmount, payload: amount });
	
	const values = React.useMemo(
		() => ({ ...state, updateGoalData, updateGoalId, goalAmount, goalGoalType, goalTargetAmount }),
		[state],
	);

	return <GoalStateContext.Provider value={{ ...values }}>{children}</GoalStateContext.Provider>;
}

function useGoal() {
	const context = React.useContext(GoalStateContext);
	if (context === undefined) {
		throw new Error('useGoal must be used within a GoalContextprovider');
	}
	return context;
}

export { GoalContextProvider, useGoal };
